<script setup lang="ts">
import { onKeyStroke, watchOnce, useScroll } from '@vueuse/core';
import { ref, watch } from 'vue';

import { KeyboardKeyCode, AppSlideUpTransition } from '@libs/ui';

interface ActivityDetailsLayoutEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<ActivityDetailsLayoutEmits>();

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));

const root = ref<HTMLDivElement>();
const headerRef = ref<HTMLElement>();
const stickyTop = ref('0px');

watchOnce(headerRef, () => {
  if (headerRef.value) {
    const topMargin = 24;
    const height = headerRef.value.offsetHeight;
    stickyTop.value = `${height + topMargin}px`;
  }
});

defineExpose({
  root,
});
const { arrivedState } = useScroll(root, {
  behavior: 'smooth',
});
const isScrollOnTop = ref(arrivedState.top);

watch(arrivedState, () => (isScrollOnTop.value = arrivedState.top));
</script>

<template>
  <AppSlideUpTransition>
    <div ref="root" class="activity-details-layout">
      <div
        ref="headerRef"
        class="header-wrapper"
        :class="{ top: isScrollOnTop }"
      >
        <div class="header">
          <slot name="header" />
        </div>
      </div>
      <div class="wrapper">
        <div class="content">
          <div class="left-panel-gallery">
            <slot name="left-panel-gallery" />
          </div>
          <div class="left-panel-content">
            <slot name="left-panel-content" />
          </div>
          <div class="right-panel">
            <div class="right-panel-scrollable">
              <slot name="right-panel-scrollable" />
            </div>
            <div
              class="right-panel-sticky"
              :style="{ '--sticky-top': stickyTop }"
            >
              <slot name="right-panel-sticky" />
            </div>
          </div>
        </div>
        <div class="footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </AppSlideUpTransition>
</template>

<style scoped lang="scss">
.activity-details-layout {
  --app-first-level-panel-width: 1130px;
  --app-first-level-panel-width-small: 930px;
  --side-padding: var(--spacing-24);

  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  // NOTE: Using dvh fixes the problem of address bar rendering over content in mobile Safari.
  // vh is here as a fallback for Firefox and old browsers, which do not support dvh.
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  z-index: var(--panel-first-level-z-index);
  background-color: var(--color-white);

  .header-wrapper {
    position: sticky;
    top: 0;
    background-color: var(--color-white);
    // NOTE: Swiper library internally sets z-index to 1 for its content.
    // Header must be above that to correctly work as sticky header.
    z-index: calc(var(--panel-first-level-z-index) + 1);
    padding: var(--spacing-32) 0 var(--spacing-8) 0;
    border-bottom: 1px solid var(--color-neutral-30);
    &.top {
      border: none;
    }
    @include for-less-or-equal-than-900-px {
      padding: var(--spacing-2) 0;
    }

    .header {
      margin: 0 auto;
      padding: 0 var(--side-padding);
      max-width: calc(
        var(--app-first-level-panel-width) - (var(--side-padding) * 2)
      );
      @include for-less-or-equal-than-1200-px {
        max-width: calc(
          var(--app-first-level-panel-width-small) - (var(--side-padding) * 2)
        );
      }
      @include for-less-or-equal-than-900-px {
        max-width: 100%;
      }
    }
  }

  .wrapper {
    position: relative;
    height: 100%;
    margin: var(--spacing-12) auto 0 auto;
    padding: 0 var(--side-padding);
    max-width: calc(
      var(--app-first-level-panel-width) - (var(--side-padding) * 2)
    );
    @include for-less-or-equal-than-1200-px {
      max-width: calc(
        var(--app-first-level-panel-width-small) - (var(--side-padding) * 2)
      );
    }
    @include for-less-or-equal-than-900-px {
      max-width: 100%;
    }

    .content {
      --right-panel-width: 430px;

      display: grid;
      grid-template-columns: auto var(--right-panel-width);
      grid-template-rows: auto auto;
      gap: var(--spacing-24);
      @include for-less-or-equal-than-900-px {
        grid-template-columns: 1fr;
        gap: var(--spacing-24) 0;
      }

      .left-panel-gallery {
        grid-column: 1 / 2;
        grid-row: 1 / 2;
      }

      .left-panel-content {
        margin-bottom: var(--spacing-32);
        grid-column: 1 / 2;
        grid-row: 2 / 3;
      }

      .right-panel {
        grid-column: 2/3;
        grid-row: 1/-1;

        .right-panel-sticky {
          position: sticky;
          top: var(--sticky-top);
        }
      }
    }
    .footer {
      position: sticky;
      margin: 0 auto;
      bottom: var(--spacing-16);
      max-width: 460px;
      width: 100%;
    }
  }
}

::-webkit-scrollbar-track {
  background: var(--color-off-white);
}
</style>
